// src/app/services/notification-settings.service.ts
import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { userDataService } from './userDataService';

export interface NotificationSettings {
  soundEnabled: boolean;
  volume: number;
}

@Injectable({
  providedIn: 'root',
})
@Injectable({ providedIn: 'root' })
export class NotificationSettingsService {
  private readonly DEFAULT_SETTINGS: NotificationSettings = {
    soundEnabled: true,
    volume: 0.1,
  };

  private _settings = signal<NotificationSettings>({ ...this.DEFAULT_SETTINGS });

  get settings() {
    return this._settings.asReadonly();
  }

  initFromUserProfile(userProfile: any): void {
    if (!userProfile) return;

    this._settings.set({
      soundEnabled: userProfile.notification_sound_enabled ?? this.DEFAULT_SETTINGS.soundEnabled,
      volume: userProfile.notification_volume ?? this.DEFAULT_SETTINGS.volume,
    });
  }

  // local only
  isSoundEnabled(): boolean {
    return this._settings().soundEnabled;
  }

  getVolume(): number {
    return this._settings().volume;
  }

  setSoundEnabledLocal(enabled: boolean): void {
    this._settings.set({ ...this._settings(), soundEnabled: enabled });
  }

  setVolumeLocal(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this._settings.set({ ...this._settings(), volume: clamped });
  }

  setVolumePercentageLocal(percentage: number): void {
    this.setVolumeLocal(percentage / 100);
  }

  getVolumePercentage(): number {
    return Math.round(this._settings().volume * 100);
  }

  // Helper to send to backend in one payload
  getPayload() {
    const s = this._settings();
    return {
      notification_sound_enabled: s.soundEnabled,
      notification_volume: s.volume,
    };
  }
}
